// useThemeMode — MỘT nguồn cho chế độ sáng/tối của toàn app.
//
// Tách khỏi pages/me/sections/AppearanceSection.jsx (15/08/2026) khi đưa nút đổi theme lên
// popup avatar: để logic nằm trong trang /me thì popup phải chép bản thứ hai, mà chép là
// mất luôn 4 luật tinh tế bên dưới — mỗi luật đều từ một sự cố thật.
//
// Luật (ĐỪNG rút gọn):
//  1. Khi KHỞI TẠO, `localStorage.theme_mode` là nguồn sự thật, KHÔNG phải BE. Sidebar áp
//     theme từ localStorage ngay lúc boot; nếu init từ BE thì mở /me lại ghi đè giá trị
//     đang lưu (BE stale 'light' đè 'dark' — user report 2026-05-30).
//  2. Chỉ NHẬN theme từ BE khi thiết bị này CHƯA TỪNG chọn — seed lần đầu cho máy mới,
//     không lật theme của máy người ta đã set.
//  3. KHÔNG sync BE ở lần chạy đầu (đang load từ storage/BE), chỉ sync khi người dùng ĐỔI.
//     Sync fail-soft: lỗi mạng không chặn UI vì theme đã áp ở local rồi.
//  4. 'auto' phải NGHE `prefers-color-scheme` và gỡ listener khi đổi sang light/dark.
//
// Ghi cả `theme_mode` (lựa chọn: light|dark|auto) lẫn `theme` (kết quả đã tính: light|dark)
// — nơi khác trong app đọc khoá `theme`.

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@host/auth';
import api from '@host/axios';

export const THEME_MODES = ['light', 'dark', 'auto'];

const isValidTheme = (v) => v === 'light' || v === 'dark' || v === 'auto';

export default function useThemeMode() {
  const { user } = useAuth();

  // Luật 1 — localStorage thắng lúc init.
  const hadLocalRef = useRef(isValidTheme(localStorage.getItem('theme_mode')));
  const [themeMode, setThemeMode] = useState(() => {
    const local = localStorage.getItem('theme_mode');
    if (isValidTheme(local)) return local;
    const fromBE = user?.preferences?.theme;
    if (isValidTheme(fromBE)) return fromBE;
    return 'light';
  });

  const skipInitialSync = useRef(true);

  // Luật 2 — chỉ nhận từ BE khi máy này chưa từng chọn.
  useEffect(() => {
    if (hadLocalRef.current) return;
    const fromBE = user?.preferences?.theme;
    if (isValidTheme(fromBE)) {
      hadLocalRef.current = true;
      setThemeMode(fromBE);
    }
  }, [user?.preferences?.theme]);

  useEffect(() => {
    const apply = () => {
      const isDark = themeMode === 'dark'
        || (themeMode === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
      document.documentElement.classList.toggle('dark', isDark);
    };
    apply();
    localStorage.setItem('theme_mode', themeMode);
    localStorage.setItem('theme', document.documentElement.classList.contains('dark') ? 'dark' : 'light');

    // Luật 3 — chỉ sync khi người dùng đổi, và fail-soft.
    if (skipInitialSync.current) {
      skipInitialSync.current = false;
    } else {
      api.put('/auth/me/preferences', { theme: themeMode }).catch((e) => {
        console.warn('[appearance] sync theme to BE failed:', e?.message || e);
      });
    }

    // Luật 4 — 'auto' bám theo hệ điều hành.
    if (themeMode === 'auto') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    return undefined;
  }, [themeMode]);

  return [themeMode, setThemeMode];
}
